<template>
  <div class="bb-email-stats">
    <transition name="fade" appear>
      <loader-fly v-if="loaderRun"></loader-fly>
    </transition>
    <h2 class="bb-page-title">Email Statistics</h2>
    <div class="bb-email-stats-header">
      <div class="bb-tabs bb-email-stats-header-tabs">
        <div class="bb-tabs__item bb-email-stats-header-tabs__item"
             :class="{'bb-tabs__item_active': activeTab === id}"
             @click="activeTab = id"
             v-for="{id, title} in tabs"
             :key="id">
          {{ title }}
        </div>
      </div>
      <transition name="fade">
        <div class="bb-email-stats-header-section-filter" v-if="activeTab">
          <FilterByDate @setDate="setFilterDate" position="left" all-name="All"/>
          <div class="bb-email-stats-header-section-filter__users">
            <v-select label="userName"
                      :options="users"
                      :value="usersSearch"
                      :clearable="true"
                      :placeholder="'User'"
                      @search="searchFilterByUser"
                      @input="setFilterByUser"/>
          </div>
        </div>
      </transition>
    </div>
    <div class="bb-email-stats-body">
      <template v-if="!activeTab">
        <BBTable :columns="columnsGeneralStats"
                 :rows="emailStatsRowData"
                 :no-data="noData"/>
      </template>
      <template v-else>
        <BBTable :columns="activeTab === 1 ? columnsStats : columnsExcludeStats"
                 :rows="emailStatsRowData"
                 :no-data="noData"
                 @filter="setFilter"/>
        <Pagination :data="fullInfo" :getQuery="getEmailStats" :limit="defLimit" class="bb-pagination"/>
      </template>
    </div>
  </div>
</template>

<script>
import BBTable from '@/components/Main/BBTable/BBTable'
import Pagination from '@/components/Main/Pagination/Pagination'
import EmailStatsApi from '@/api/EmailStats/api'
import {EMAIL_STATUS_EXCLUDED, EMAIL_STATUS_PENDING, EMAIL_STATUS_REPLIED} from '@/constants/emailStatus'
import emailStatsTableColumns from '@/constants/TablesOptions/emailStatsTable'
import emailStatsExcludeTableColumns from '@/constants/TablesOptions/emailStatsExcludeTable'
import emailStatsTotalTableColumns from '@/constants/TablesOptions/emailStatsTotalTable'
import {get, find, orderBy, capitalize} from 'lodash'
import FilterByDate from '../Main/FilterByDate/FilterByDate'
import vSelect from 'vue-select'
import UsersListApi from '@/api/UsersList/api'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'

export default {
  name: 'EmailStats',
  components: {FilterByDate, Pagination, BBTable, vSelect, LoaderFly},
  data () {
    return {
      loaderRun: false,
      noData: false,
      columnsStats: emailStatsTableColumns,
      columnsExcludeStats: emailStatsExcludeTableColumns,
      columnsGeneralStats: emailStatsTotalTableColumns,
      defLimit: 30,
      activeTab: 1,
      fullInfo: {},
      tabs: [
        {id: 1, title: 'Response rate'},
        {id: 2, title: 'Marked as excluded'},
        {id: 0, title: 'General statistics'}
      ],
      reasons: [
        {id: 1, title: 'Not a client'},
        {id: 2, title: 'No reply required'},
        {id: 3, title: 'Replied by messenger'},
        {id: 4, title: 'Attended a meeting'}
      ],
      filter: {
        status: []
      },
      filterByDate: '',
      usersSearch: '',
      filterByUser: '',
      filterUserId: ''
    }
  },
  computed: {
    mainScroll () {
      return this.$store.getters.mainScroll
    },
    emailStatsRowData: {
      get () {
        return this.$store.getters.emailStatsRowData
      },
      set (data) {
        this.$store.dispatch('setEmailStatsRowsData', data)
      }
    },
    currentStatus () {
      return this.activeTab === 1 ? [EMAIL_STATUS_PENDING, EMAIL_STATUS_REPLIED] : [EMAIL_STATUS_EXCLUDED]
    },
    users: {
      get: function () {
        return this.$store.getters.usersList
      },
      set: function (data) {
        this.$store.dispatch('setUsersList', data)
      }
    },
    filterByStatus () {
      return this.filter['status'].length > 0 ? this.filter['status'] : ''
    }
  },
  methods: {
    setFilter ({checked, id, prop}) {
      const index = this.filter[prop].indexOf(id)
      if (checked) {
        this.filter[prop].push(id)
      } else if (index !== -1) {
        this.filter[prop].splice(index, 1)
      }
      this.getEmailStats({})
    },
    getEmailStatsTotal ({page}) {
      this.loaderRun = true
      EmailStatsApi.getEmailStatsTotal({page, limit: this.defLimit})
        .then((data = {}) => {
          this.loaderRun = false
          this.emailStatsRowData = data.data
          this.noData = !data.data.length
        })
        .catch(err => {
          this.noData = true
          console.error(err)
          this.loaderRun = false
        })
    },
    getEmailStats ({page}) {
      this.scrollTop()
      this.loaderRun = true
      EmailStatsApi.getEmailStats({
        page,
        limit: this.defLimit,
        status: this.filterByStatus || this.currentStatus,
        search: this.usersSearch,
        filterByDate: this.filterByDate,
        user: this.filterByUser
      })
        .then(({data = {}}) => {
          this.loaderRun = false
          this.fullInfo = data
          this.noData = !data.usermessages || data.usermessages.length === 0
          const regexp = /(^.+)(\s)(<)(.+)(>)/gmi
          this.emailStatsRowData = data.usermessages.map(item => {
            item.excludedReason = find(this.reasons, {id: item.excludedReason})
            const resultMatch = [...item.deliveryFromValue.matchAll(regexp)]
            item.deliveryFromValue = get(resultMatch, [0, 4], item.deliveryFromValue)
            return item
          })
          if (data.usermessages.length === 0) this.noData = data.usermessages.length === 0
        })
        .catch(err => {
          this.noData = true
          this.loaderRun = false
          console.error(err)
        })
    },
    setFilterDate (range) {
      this.filterByDate = range
      this.getEmailStats({})
    },
    getUserList () {
      UsersListApi.getUsersList({limit: 1000, assigned: false})
        .then((resp) => {
          this.users = resp.data.users.map(user => {
            user.userName = capitalize(user.username)
            return user
          })
        })
        .catch((err) => {
          console.error(err)
        })
    },
    searchFilterByUser (word) {
      if (!word) {
        this.users = orderBy(this.users, ['username'], ['asc'])
      } else {
        const reg = new RegExp('^' + word, 'gmi')
        this.users = this.users.sort((a, b) => {
          return reg.test(b.username) - reg.test(a.username)
        })
      }
    },
    setFilterByUser (item) {
      this.filterUserId = item ? item.id : ''
      this.filterByUser = item ? item.id : ''
      this.usersSearch = item ? item.userName : ''
      if (this.activeTab !== 0) this.getEmailStats({})
    },
    scrollTop () {
      const scroll = get(this.mainScroll, 'target', {})
      scroll.scrollTop = 0
    }
  },
  watch: {
    activeTab (newVal) {
      newVal === 0 ? this.getEmailStatsTotal({}) : this.getEmailStats({})
    }
  },
  beforeMount () {
    this.getEmailStats({})
    this.getUserList()
  }
}
</script>

<style lang="scss" scoped>
@import "../../sass/variables";
@import "../../sass/mixins";

.bb-email-stats {
  @include global-font;

  &-header {
    margin-top: 15px;
    display: flex;
    justify-content: space-between;

    &-section-filter {
      display: flex;

      &__users {
        width: 233px;
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 300;
        height: 42px;
        background-color: $color-white;
        border-radius: 5px;
        margin-left: 10px;
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/EmailStats/EmailStats.vue